import React, { useState } from "react";
import { Typography, Row, Col, Card, Button, Collapse } from "antd";
import {
  CheckOutlined,
  TagFilled,
  FireOutlined,
  UpOutlined,
  DownOutlined,
} from "@ant-design/icons";
import { plans, bundles } from "../../data/data";
import "./Plans.scss";

const { Paragraph } = Typography;

const SubscriptionSelector = ({ selectedPlan, setSelectedPlan }) => {
  return (
    <div className="subscription-selector-wrapper">
      <div className="subscription-selector">
        <div
          className={`plan ${
            selectedPlan === "subscriptions" ? "selected" : ""
          }`}
          onClick={() => setSelectedPlan("subscriptions")}
        >
          <span className="plan-title">Subscriptions</span>
          <span className="plan-subtitle">Best value</span>
        </div>
        <div
          className={`plan ${selectedPlan === "bundles" ? "selected" : ""}`}
          onClick={() => setSelectedPlan("bundles")}
        >
          <span className="plan-title">Bundles</span>
          <span className="plan-subtitle">Pay as you go</span>
        </div>
      </div>
    </div>
  );
};

const FeatureList = ({ features }) => (
  <ul>
    {features.map((feature, i) => (
      <li key={i}>
        <span className="feature-icon">
          <CheckOutlined />
        </span>
        <span>{feature}</span>
      </li>
    ))}
  </ul>
);

const TableData = ({ tableData }) => (
  <div className="table-data">
    {tableData.map((data, i) => (
      <span key={i} className="table-row">
        <span className="service">{data.service}</span>
        <input className="value" type="number" value={data.value} readOnly />
      </span>
    ))}
  </div>
);

const PlanCard = ({ plan, index }) => {
  const renderFeatures = <FeatureList features={plan.features} />;

  const renderTableData = <TableData tableData={plan.tableData} />;

  const renderMobileFeatures = (
    <Collapse
      expandIconPosition="right"
      expandIcon={({ isActive }) =>
        isActive ? <UpOutlined /> : <DownOutlined />
      }
    >
      <Collapse.Panel
        header={
          plan.tableData
            ? "Includes any combination of these services:"
            : plan.featuresHeading
        }
        key="1"
      >
        {plan.tableData && renderTableData}
        <div className="features">{renderFeatures}</div>
      </Collapse.Panel>
    </Collapse>
  );

  return (
    <Col xs={24} sm={12} md={12} lg={6} key={index}>
      <Card className="plan-card">
        <div className="card-content">
          <span className="card-subtitle">{plan.subtitle}</span>
          <span className="card-title">{plan.title}</span>
          <Paragraph className="card-description">
            {plan.description}
            {plan.discount && (
              <div className="discount-wrapper">
                <TagFilled className="discount-icon" />
                <Paragraph className="discount">{plan.discount}</Paragraph>
              </div>
            )}
          </Paragraph>
          <span className="card-price">
            {plan.price}
            <span className="annual-period">{plan.annualPeriod}</span>
          </span>
          <Paragraph className="monthly-period">{plan.monthlyPeriod}</Paragraph>
          <Button
            type="primary"
            className={`${plan.buttonClassName} card-button`}
            block
            onClick={() => {
              window.location.href = plan.buttonLink;
            }}
          >
            {plan.buttonText}
          </Button>
        </div>

        <div className="features-wrapper">
          {/* Desktop view */}
          <div className="desktop-features hidden md:block">
            {plan.tableData && (
              <>
                <span className="features-heading">
                  Includes any combination of these services:
                </span>
                {renderTableData}
              </>
            )}
            <>
              <span className="features-heading">{plan.featuresHeading}</span>
              <div className="features">{renderFeatures}</div>
            </>
          </div>

          {/* Mobile view */}
          <div className="mobile-features md:hidden">
            {renderMobileFeatures}
          </div>
        </div>
      </Card>
    </Col>
  );
};

const BundleCard = ({ bundle, index }) => {
  const renderFeatures = <FeatureList features={bundle.features} />;
  const renderMobileFeatures = (
    <Collapse
      expandIconPosition="right"
      expandIcon={({ isActive }) =>
        isActive ? <UpOutlined /> : <DownOutlined />
      }
    >
      <Collapse.Panel header={bundle.featuresHeading} key="1">
        <div className="features">{renderFeatures}</div>
      </Collapse.Panel>
    </Collapse>
  );

  return (
    <Col xs={24} sm={12} md={12} lg={6} key={index}>
      <Card className="plan-card">
        <div className="card-content">
          {bundle.alert ? (
            <span className="bundle-alert">
              <FireOutlined className="fire-icon" />
              <span> {bundle.alert}</span>
            </span>
          ) : (
            <div className="no-alert" />
          )}

          <span className="card-title bundle">{bundle.title}</span>
          <Paragraph className="card-description">
            {bundle.description}
          </Paragraph>
          <span className="card-price">
            {bundle.price}
            <span className="annual-period">/product</span>
          </span>
          <Paragraph className="monthly-period">One-time purchase</Paragraph>
          <Button type="primary" className={`card-button`} block>
            {bundle.buttonText}
          </Button>
        </div>

        <div className="features-wrapper">
          {/* Desktop view */}
          <div className="desktop-features hidden md:block">
            <div className="features">
              <span className="features-heading">{bundle.featuresHeading}</span>
              {renderFeatures}
            </div>
          </div>

          {/* Mobile view */}
          <div className="mobile-features md:hidden">
            {renderMobileFeatures}
          </div>
        </div>
      </Card>
    </Col>
  );
};

const Plans = () => {
  const [selectedPlan, setSelectedPlan] = useState("subscriptions");

  return (
    <div className="pricing-plans-wrapper">
      <div className="pricing-plans">
        <span className="title">
          Flexible pricing to easily bring your products to life with 3D and AR
        </span>
        <Paragraph className="description">
          ALL3D is the easiest way to create premium, immersive 3D imagery and
          experiences for ecommerce.
        </Paragraph>

        <SubscriptionSelector
          selectedPlan={selectedPlan}
          setSelectedPlan={setSelectedPlan}
        />

        {selectedPlan === "subscriptions" ? (
          <>
            <Row gutter={[16, 16]}>
              {plans.map((plan, index) => (
                <PlanCard key={index} plan={plan} index={index} />
              ))}
            </Row>
            <Paragraph className="footnote">
              Subscriptions can include any combination of models and images
              within your paid quota. The quantities shown above reflect a
              potential combination of each plan's annual quota. All
              subscriptions require an annual contract.{" "}
              <a href="#faqs" className="footnote-link">See FAQs</a>
            </Paragraph>
          </>
        ) : (
          <>
            <Row gutter={[16, 16]}>
              {bundles.map((bundle, index) => (
                <BundleCard key={index} bundle={bundle} index={index} />
              ))}
            </Row>
            <Paragraph className="footnote">
              Each bundle price is per product (SKU). You may increase the
              quantity needed in checkout. See FAQs
            </Paragraph>
          </>
        )}
      </div>
    </div>
  );
};

export default Plans;
